/**
 * RemoteBooksGate.js
 * Opens the app on the SHARED online books (multi-device sync, step 4). Rendered by index.js
 * instead of <App/> whenever the build has Supabase settings (utils/remoteStorage.js's
 * remoteConfigFromEnv); without them the app runs on localStorage exactly as before.
 *
 * Before the app appears it loads the books once:
 *   - "Memuat data…" while loading;
 *   - any failure → an error screen naming the problem, with "Coba lagi". It NEVER falls back to
 *     sample or empty data — a failed load opening on empty books, and the next save then
 *     overwriting everyone's real data, is exactly what this screen exists to prevent;
 *   - stored data in a NEWER format than this copy of the app understands → "muat ulang",
 *     no app at all (opening it could only ever lose work);
 *   - the online copy never set up yet (data null) → creates the starting books
 *     (storage.js's buildStarterBooks: branches + cars) with a version-checked save;
 *   - stored data in an OLDER format → upgraded by the very same storage.js code localStorage
 *     data goes through (prepareLoadedData), then saved back (the database backs up the old
 *     format first) — before the app opens, so everyone moves to the new format together.
 * If someone else set up or upgraded the books in the same moment (a "conflict" answer), it
 * simply loads again and uses theirs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import App from "./App";
import { createRemoteStore } from "./utils/remoteStorage";
import { NORM_VERSION, buildStarterBooks, prepareLoadedData } from "./utils/storage";
import { loadDeviceLabel } from "./utils/devicePrefs";

const MAX_ATTEMPTS = 3; // load → conflict → load again, at most this many rounds

// Plain-language explanation for each way loading can fail (see remoteStorage.js's error kinds).
export function loadErrorText(error) {
  switch (error && error.kind) {
    case "offline": return "Tidak dapat terhubung ke server. Periksa koneksi internet, lalu coba lagi.";
    case "timeout": return "Server tidak menjawab. Periksa koneksi internet, lalu coba lagi.";
    case "auth": return "Server menolak kunci akses aplikasi ini (pengaturan situs salah). Hubungi developer.";
    case "not_found": return "Data bersama tidak ditemukan di server (pengaturan situs salah). Hubungi developer.";
    case "bad_response": return `Jawaban server tidak dikenal: ${error.message} Hubungi developer.`;
    case "rejected": return `${error.message} Hubungi developer.`;
    case "retry_limit": return "Data sedang diubah dari perangkat lain berulang kali. Coba lagi sebentar lagi.";
    default: return `Server bermasalah${error && error.status ? ` (kode ${error.status})` : ""}. Coba lagi sebentar lagi.`;
  }
}

/** Load (and if needed set up or upgrade) the shared books. Resolves to one of:
 *  { phase:"ready", data, version } | { phase:"outdated" } | { phase:"error", error } */
export async function openSharedBooks(store, savedBy) {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const r = await store.getState();
    if (!r.ok) return { phase: "error", error: r.error };
    if (r.normVersion !== null && r.normVersion > NORM_VERSION) return { phase: "outdated" };

    let data;
    if (r.data === null) {
      data = buildStarterBooks();
    } else {
      const { merged, migrated } = prepareLoadedData(r.data);
      if (migrated === merged) return { phase: "ready", data: migrated, version: r.version };
      data = migrated;
    }

    // Starting books, or upgraded data: save before anyone works on it.
    const s = await store.saveState({ expectedVersion: r.version, data, normVersion: NORM_VERSION, savedBy });
    if (s.ok) return { phase: "ready", data, version: s.version };
    if (s.reason === "conflict") continue; // someone else got there first — use theirs
    if (s.reason === "outdated_app") return { phase: "outdated" };
    if (s.reason === "too_large" || s.reason === "invalid") {
      return { phase: "error", error: { kind: "rejected", message: s.reason === "too_large" ? "Data terlalu besar untuk disimpan." : "Server menolak data ini." } };
    }
    return { phase: "error", error: s.error };
  }
  return { phase: "error", error: { kind: "retry_limit" } };
}

function GateScreen({ title, children }) {
  return (
    <div className="gate-screen">
      <div className="gate-screen__box">
        <div className="gate-screen__brand">Buku Kas IJM</div>
        {title && <h1 className="gate-screen__title">{title}</h1>}
        {children}
      </div>
    </div>
  );
}

// pollMs: how often the open app checks for other people's changes (step 5b) — 30 s in real use;
// tests pass a short one.
export default function RemoteBooksGate({ config, store: storeProp = null, pollMs = 30000 }) {
  const store = useMemo(() => storeProp || createRemoteStore(config), [storeProp, config]);
  const [state, setState] = useState({ phase: "loading" });
  const runRef = useRef(0);

  const load = useCallback(async () => {
    const run = ++runRef.current;
    setState({ phase: "loading" });
    const result = await openSharedBooks(store, loadDeviceLabel().label || null);
    if (run === runRef.current) setState(result); // ignore a stale run (StrictMode, retry clicks)
  }, [store]);

  useEffect(() => { load(); }, [load]);

  if (state.phase === "ready") {
    return <App initialData={state.data} remote={{ store, version: state.version, pollMs }} />;
  }
  if (state.phase === "loading") {
    return (
      <GateScreen>
        <div className="gate-screen__spinner" aria-hidden="true" />
        <p className="gate-screen__text" role="status">Memuat data…</p>
      </GateScreen>
    );
  }
  if (state.phase === "outdated") {
    return (
      <GateScreen title="Versi aplikasi baru tersedia">
        <p className="gate-screen__text">
          Data bersama sudah memakai versi aplikasi yang lebih baru dari yang terbuka di sini.
          Muat ulang halaman untuk memakai versi terbaru.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Muat ulang halaman</button>
      </GateScreen>
    );
  }
  return (
    <GateScreen title="Data belum bisa dimuat">
      <p className="gate-screen__text" role="alert">{loadErrorText(state.error)}</p>
      <p className="gate-screen__hint">Tidak ada data yang diubah. Aplikasi tidak dibuka sampai data berhasil dimuat.</p>
      <button type="button" className="btn btn-primary" onClick={load}>Coba lagi</button>
    </GateScreen>
  );
}
